import { useEffect, useState } from "react";
import { fetchParties, fetchOverview } from "@/lib/api";
import PartyCard from "@/components/PartyCard";
import Seo from "@/components/Seo";
import { Search } from "lucide-react";

export default function Parties() {
    const [parties, setParties] = useState([]);
    const [overview, setOverview] = useState([]);
    const [query, setQuery] = useState("");
    const [sortBy, setSortBy] = useState("trust");

    useEffect(() => {
        Promise.all([fetchParties(), fetchOverview()]).then(([p, o]) => {
            setParties(p);
            setOverview(o);
        });
    }, []);

    const statsById = Object.fromEntries(overview.map((o) => [o.party_id, o]));

    let filtered = parties.filter(
        (p) =>
            p.name.toLowerCase().includes(query.toLowerCase()) ||
            p.ideology.toLowerCase().includes(query.toLowerCase()) ||
            p.leader.toLowerCase().includes(query.toLowerCase())
    );

    if (sortBy === "trust") {
        filtered = [...filtered].sort(
            (a, b) => (statsById[b.id]?.trust_score || 0) - (statsById[a.id]?.trust_score || 0)
        );
    } else if (sortBy === "seats") {
        filtered = [...filtered].sort((a, b) => (b.seats || 0) - (a.seats || 0));
    } else if (sortBy === "name") {
        filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name, "da"));
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="parties-page">
            <Seo
                title="Alle partier i Folketinget"
                description="Overblik over alle 10 partier i Folketinget — Socialdemokratiet, Venstre, Moderaterne, SF, Danmarksdemokraterne, Liberal Alliance, Konservative, Enhedslisten, Radikale Venstre og Dansk Folkeparti. Se tillidsscore, mandater, ideologi og ledere."
                keywords="danske partier, Folketinget, Socialdemokratiet, Venstre, Moderaterne, SF, Danmarksdemokraterne, Liberal Alliance, Konservative, Enhedslisten, Radikale Venstre, Dansk Folkeparti, mandater, partiledere"
                path="/partier"
            />
            <div className="mb-10">
                <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                    Alle partier
                </span>
                <h1 className="font-serif-dm text-5xl text-royal mt-2">
                    Partierne i Folketinget
                </h1>
                <p className="text-[#4F5A7A] mt-3 max-w-2xl">
                    Få et hurtigt overblik over alle partier, deres tillidsscore og hvor
                    mange løfter de har holdt eller brudt.
                </p>
            </div>

            <div className="flex flex-col md:flex-row gap-4 mb-8">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#4F5A7A]" />
                    <input
                        type="text"
                        placeholder="Søg efter parti, ideologi eller leder..."
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 border border-[#E5E8F0] rounded-full focus:outline-none focus:border-royal focus:ring-2 focus:ring-royal/15 text-sm"
                        data-testid="party-search-input"
                    />
                </div>
                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-5 py-3 border border-[#E5E8F0] rounded-full text-sm bg-white focus:outline-none focus:border-royal"
                    data-testid="party-sort-select"
                >
                    <option value="trust">Sortér: Højeste tillid</option>
                    <option value="seats">Sortér: Flest mandater</option>
                    <option value="name">Sortér: Alfabetisk</option>
                </select>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((p) => (
                    <PartyCard key={p.id} party={p} stats={statsById[p.id]} />
                ))}
            </div>

            {filtered.length === 0 && (
                <div className="text-center py-20 text-[#4F5A7A]" data-testid="no-parties">
                    Ingen partier matcher din søgning.
                </div>
            )}
        </div>
    );
}
